import { Space, Typography, Switch } from 'antd';
import { useTranslation } from 'react-i18next';

import type React from 'react';

type NullableBooleanValue = boolean | null;

type Props = {
  value?: NullableBooleanValue;
  onChange?: (v: NullableBooleanValue) => void;
  disabled?: boolean;
};

/**
 * Tri-state nullable boolean: Switch + explicit state label + Clear to unset (Option A from migration plan).
 * We do not set checkedChildren/unCheckedChildren: when value is null the switch is off; showing "NO"
 * inside the track would falsely imply a definitive negative answer (see antd Switch "Text & icon" demo).
 */
const NullableBooleanSwitch: React.FC<Props> = ({
  value,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const v = value === undefined ? null : value;
  const label = v === null ? t('NOT_SET') : v ? t('YES') : t('NO');

  return (
    <Space align="center" wrap>
      <Switch
        checked={v === true}
        disabled={disabled}
        onChange={(checked) => {
          onChange?.(checked ? true : false);
        }}
      />
      <Typography.Text type="secondary">{label}</Typography.Text>
      {(v === true || v === false) && (
        <Typography.Link disabled={disabled} onClick={() => onChange?.(null)}>
          {t('CLEAR')}
        </Typography.Link>
      )}
    </Space>
  );
};

export default NullableBooleanSwitch;
